import React, { useState, useEffect } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { CustomerTable } from "@/components/crm/CustomerTable";
import { CustomerDetailsPanel } from "@/components/crm/CustomerDetailsPanel";
import { Button } from "@/components/ui/button";
import { PlusCircle, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddCustomerForm } from "@/components/crm/AddCustomerForm";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  appointmentCount: number;
  lastVisit: Date | null;
  notes: string;
  createdAt: Date;
  smsConsent?: boolean;
  marketingConsent?: boolean;
}

const CustomersPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // In a real implementation, this would fetch from your database
    loadCustomers();
    document.title = "Customer Management | Figgy.link";
  }, []);

  const loadCustomers = () => {
    setLoading(true);
    // Mock data - replace with actual API call in production
    setTimeout(() => {
      setCustomers([
        {
          id: "1",
          name: "John Doe",
          email: "john@example.com",
          phone: "(555) 123-4567",
          appointmentCount: 3,
          lastVisit: new Date(2023, 3, 15),
          notes: "Prefers evening appointments",
          createdAt: new Date(2022, 10, 5)
        },
        {
          id: "2",
          name: "Jane Smith",
          email: "jane@example.com",
          phone: "(555) 987-6543",
          appointmentCount: 7,
          lastVisit: new Date(2023, 4, 22),
          notes: "Allergic to certain products",
          createdAt: new Date(2022, 8, 12)
        },
        {
          id: "3",
          name: "Mike Johnson",
          email: "mike@example.com",
          phone: "(555) 456-7890",
          appointmentCount: 1,
          lastVisit: null,
          notes: "",
          createdAt: new Date(2023, 1, 20)
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const handleAddCustomer = async (customer: {
    name: string;
    email: string;
    phone: string;
    notes: string;
    smsConsent: boolean;
    marketingConsent: boolean;
  }) => {
    try {
      // Split name basic
      const [firstName, ...rest] = customer.name.split(' ');
      const lastName = rest.join(' ');

      // Resolve business_id for current user
      const { data: profile } = await supabase
        .from('business_profiles')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (!profile?.id) {
        toast.error('Could not resolve business profile.');
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .insert({
          first_name: firstName,
          last_name: lastName,
          email: customer.email || null,
          phone: customer.phone || null,
          notes: customer.notes || null,
          user_id: profile.id, // tenant key as used elsewhere in app
          sms_consent: !!customer.smsConsent,
          marketing_consent: !!customer.marketingConsent,
          consent_updated_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        })
        .select('id, first_name, last_name, email, phone')
        .single();

      if (error) throw error;

      const newCustomer: Customer = {
        id: data.id,
        name: `${firstName} ${lastName}`.trim(),
        email: customer.email,
        phone: customer.phone,
        notes: customer.notes,
        appointmentCount: 0,
        lastVisit: null,
        createdAt: new Date(),
        smsConsent: customer.smsConsent,
        marketingConsent: customer.marketingConsent
      };

      setCustomers([newCustomer, ...customers]);
      setAddDialogOpen(false);
      toast.success("Customer added successfully");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to add customer');
    }
  };

  const handleUpdateCustomer = async (updatedCustomer: Customer) => {
    try {
      // Persist updates including sms/marketing consent
      const [firstName, ...rest] = updatedCustomer.name.split(' ');
      const lastName = rest.join(' ');

      const { error } = await supabase
        .from('clients')
        .update({
          first_name: firstName || null,
          last_name: lastName || null,
          email: updatedCustomer.email || null,
          phone: updatedCustomer.phone || null,
          notes: updatedCustomer.notes || null,
          sms_consent: updatedCustomer.smsConsent ?? null,
          marketing_consent: updatedCustomer.marketingConsent ?? null,
          consent_updated_at: new Date().toISOString()
        })
        .eq('id', updatedCustomer.id);

      if (error) throw error;

      setCustomers(customers.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
      setSelectedCustomer(updatedCustomer);
      toast.success("Customer updated successfully");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to update customer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold flex items-center">
                <Users className="mr-2 h-6 w-6 text-figgy-500" />
                Customers
              </h1>
              <p className="text-muted-foreground">
                Manage your customer relationships and history
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button 
                className="bg-figgy-500 hover:bg-figgy-600" 
                onClick={() => setAddDialogOpen(true)}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Customer
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <CustomerTable 
                customers={customers}
                onSelectCustomer={(customer) => setSelectedCustomer(customer)}
                isLoading={loading}
              />
            </div>
            <div className="lg:col-span-1">
              {selectedCustomer ? (
                <CustomerDetailsPanel 
                  customer={selectedCustomer}
                  onUpdateCustomer={handleUpdateCustomer}
                />
              ) : (
                <div className="bg-white rounded-lg border p-6 h-full flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Users className="mx-auto h-12 w-12 mb-4 opacity-30" />
                    <p>Select a customer to view their details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <AddCustomerForm onSubmit={handleAddCustomer} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
